import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  PencilIcon,
  CalendarIcon,
  CurrencyRupeeIcon,
  ClipboardDocumentListIcon,
  CircleStackIcon,
  TagIcon,
  AdjustmentsHorizontalIcon,
} from "@heroicons/react/24/outline";
import api from "../../api";

const KittyForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    kitty_name: "",
    kitty_type: "custom",
    start_date: "",
    end_date: "",
    target_amount: 0,
    collected_amount: 0,
    number_of_tokens: "",
    status: "active",
    description: "",
  });

  const [loading, setLoading] = useState(!!id);

  useEffect(() => {
  if (id) {
    api.get(`/kitties/${id}`).then((res) => {
      const kitty = res.data.data || res.data; 
      setForm({
        ...form,
        ...kitty,
      });
      setLoading(false);
    });
  }
}, [id]);


  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (id) {
        await api.put(`/kitties/${id}`, form);
      } else {
        await api.post("/kitties", form);
      }
      navigate("/jewellers/kittyList");
    } catch (err) {
      console.error("Save error:", err.response?.data || err.message);
      alert("Failed to save kitty. Check console for details.");
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-600">Loading kitty details...</div>;
  }

  return (
    <div className="p-8 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center mb-8 gap-2">
        <TagIcon className="h-6 w-6 text-purple-600" />
        <h1 className="text-3xl font-bold text-gray-800">
          {id ? "Edit Kitty" : "Add Kitty"}
        </h1>
      </div>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        className="bg-white shadow-lg rounded-2xl p-8 space-y-6 border border-gray-100"
      >
        {/* Kitty Name */}
        <div className="relative">
          <label className="absolute -top-2 left-3 bg-white px-2 text-gray-500 text-sm flex items-center gap-1">
            <PencilIcon className="h-4 w-4 text-purple-500" /> Kitty Name
          </label>
          <input type="text" name="kitty_name" value={form.kitty_name} onChange={handleChange} required placeholder="Enter kitty name"
            className="w-full border border-gray-300 rounded-xl px-4 py-3 pl-11 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 placeholder-gray-400"
          />
        </div>

        {/* Kitty Type */}
        <div className="relative">
          <label className="absolute -top-2 left-3 bg-white px-2 text-gray-500 text-sm flex items-center gap-1">
            <AdjustmentsHorizontalIcon className="h-4 w-4 text-purple-500" /> Kitty Type
          </label>
          <select name="kitty_type" value={form.kitty_type} onChange={handleChange} className="w-full border border-gray-300 rounded-xl px-4 py-3 pl-11 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 text-gray-700">
            <option value="gold">Gold</option>
            <option value="silver">Silver</option>
            <option value="custom">Custom</option>
          </select>
        </div>

        {/* Dates */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="relative">
            <label className="absolute -top-2 left-3 bg-white px-2 text-gray-500 text-sm flex items-center gap-1">
              <CalendarIcon className="h-4 w-4 text-purple-500" /> Start Date
            </label>
            <input type="date" name="start_date" value={form.start_date || ""} onChange={handleChange} className="w-full border border-gray-300 rounded-xl px-4 py-3 pl-11 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500" />
          </div>

          <div className="relative">
            <label className="absolute -top-2 left-3 bg-white px-2 text-gray-500 text-sm flex items-center gap-1">
              <CalendarIcon className="h-4 w-4 text-purple-500" /> End Date
            </label>
            <input type="date" name="end_date" value={form.end_date || ""} onChange={handleChange} className="w-full border border-gray-300 rounded-xl px-4 py-3 pl-11 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500" />
          </div>
        </div>

        {/* Target Amount */}
        <div className="relative">
          <label className="absolute -top-2 left-3 bg-white px-2 text-gray-500 text-sm flex items-center gap-1">
            <CurrencyRupeeIcon className="h-4 w-4 text-purple-500" /> Target Amount
          </label>
          <input type="number" name="target_amount" value={form.target_amount} onChange={handleChange} required placeholder="Enter target amount" className="w-full border border-gray-300 rounded-xl px-4 py-3 pl-11 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 placeholder-gray-400" />
        </div>

        {/* Total Number of Tokens */}
        <div className="relative">
          <label className="absolute -top-2 left-3 bg-white px-2 text-gray-500 text-sm flex items-center gap-1">
            <CircleStackIcon className="h-4 w-4 text-purple-500" /> Total Number of Tokens
          </label>
          <input type="number" name="number_of_tokens" value={form.number_of_tokens} onChange={handleChange} required placeholder="Enter target amount" className="w-full border border-gray-300 rounded-xl px-4 py-3 pl-11 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 placeholder-gray-400" />
        </div>

        {/* Description */}
        <div className="relative">
          <label className="absolute -top-2 left-3 bg-white px-2 text-gray-500 text-sm flex items-center gap-1">
            <ClipboardDocumentListIcon className="h-4 w-4 text-purple-500" /> Notes
          </label>
          <textarea name="description" value={form.description} onChange={handleChange} rows="4" placeholder="Write notes or description..." className="w-full border border-gray-300 rounded-xl px-4 py-3 pl-11 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 placeholder-gray-400 resize-none" />
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button type="submit" className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-8 py-3 rounded-xl shadow-md transition-all duration-300 active:scale-95">
            Save Kitty
          </button>
        </div>
      </form>
    </div>
  );
};

export default KittyForm;
